import { Box, Flex, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { financialCovenants as commonFinancialCovenants } from '@finley-technologies/covenant-calculations';
import {
  CreditAgreement,
  Util,
  typeguards as finleyTypeguards,
} from '@finley-technologies/finley-types';

import { IntermediateFieldsQueryVariablesSidebar } from '../covenantIntermediateField/IntermediateFieldsQueryVariablesSidebar';
import { IntermediateFieldsView } from '../covenantIntermediateField/IntermediateFieldView';
import { CovenantValueContainer } from '../covenantValue/CovenantValueView';
import { ThresholdIntermediateFieldsView } from '../covenantValue/ThresholdValueContainer';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type FinancialCovenantEditCovenantValueContainerProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  triggerDate: Util.FullDateStr;
  intermediateFields: CreditAgreement.Config.CommonIntermediateField.IntermediateField[];
};
export const FinancialCovenantEditCovenantValueContainer = ({
  wipConfig,
  fullPropertyKey: financialCovenantFullPropertyKey,
  triggerDate,
  intermediateFields,
}: FinancialCovenantEditCovenantValueContainerProps) => {
  const toast = useToast();

  const onSave = useCallback(
    async ({
      field,
      saveIdx,
      fullPropertyKey,
      configPropertyKey,
      setIsSaveLoading,
    }: {
      field: CreditAgreement.Config.CommonIntermediateField.IntermediateField;
      saveIdx: number;
      fullPropertyKey: string;
      configPropertyKey: string;
      setIsSaveLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    }) => {
      if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
        return;
      }

      // financialCovenants[idx]._config_
      const originalConfig = getIn(
        wipConfig.formProps.values,
        configPropertyKey,
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isFinancialCovenantConfig(
          originalConfig,
        ),
        'Expected Financial Config structure while saving',
      );

      // Trim leading "=", formula engine does not detect this error until runtime.
      const sanitizedField: CreditAgreement.Config.CommonIntermediateField.IntermediateField = field.config.formula.startsWith(
        '=',
      )
        ? {
            ...field,
            config: { ...field.config, formula: field.config.formula.slice(1) },
          }
        : field;

      const config = _.cloneDeep(originalConfig);
      config.intermediateFields = config.intermediateFields.map(
        (intField, index) => (index === saveIdx ? sanitizedField : intField),
      );

      const configWithTriggerDate = commonFinancialCovenants.attachTriggerDate({
        config,
        triggerDate,
      });
      if (!configWithTriggerDate.success) {
        toast({
          title: 'Cannot save invalid intermediate field configuration.',
          description: configWithTriggerDate.errors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      const validateResults = commonFinancialCovenants.validate.validateFinancialCovenantConfig(
        { config: configWithTriggerDate.config },
      );
      if (validateResults.errors.length > 0) {
        toast({
          title: 'Cannot save invalid intermediate field configuration.',
          description: validateResults.errors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: fullPropertyKey,
        updatedProperty: sanitizedField,
        setIsSaveLoading,
      });
    },
    [wipConfig, triggerDate, toast],
  );

  const configPropertyKey = `${financialCovenantFullPropertyKey}._config_`;
  const intermediateFieldPropertyKey = `${financialCovenantFullPropertyKey}._config_.intermediateFields`;

  return (
    <Flex>
      <Box width="100%">
        <IntermediateFieldsView
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
        <CovenantValueContainer
          covenantType="financial"
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
        <ThresholdIntermediateFieldsView
          covenantType="financial"
          wipConfig={wipConfig}
          intermediateFields={intermediateFields}
          configPropertyKey={configPropertyKey}
          intermediateFieldPropertyKey={intermediateFieldPropertyKey}
          onSave={onSave}
        />
      </Box>
      {!!wipConfig && wipConfig.operationMode === 'lender' ? (
        <IntermediateFieldsQueryVariablesSidebar
          covenantType="financial"
          wipConfig={wipConfig}
          configPropertyKey={configPropertyKey}
        />
      ) : null}
    </Flex>
  );
};
